import { defineStore } from 'pinia'
import { analyticsService } from '@/services/analytics'

import { type IUser, type IUserData, User } from '@/models/user'
import authService from '@/services/auth/auth'
import { SystemError } from '@/models/error'

import { AUTH0_CLIENT_ID, AUTH0_DOMAIN, MICROSOFT_CLIENT_ID, MICROSOFT_REDIRECT_URI, MICROSOFT_TENANT_ID, REDIRECT_URI } from './auth.constants'
import type { IAuthState } from './auth.types'

// Pinia store for managing auth-related states and actions
export const useAuthStore = defineStore('auth', {
  state: (): IAuthState => ({
    user: null,
    accessToken: localStorage.getItem('token'),
  }),

  getters: {
    isAuthenticated(): boolean {
      return !!this.accessToken
    },

    // Getter to retrieve current authenticate user from the store
    getUser(state: IAuthState): IUser | null {
      let user = null
      if (state.user != null) {
        user = new User(state.user)
      }
      return user
    }
  },

  actions: {
    async authorize(options?: { email?: string; screen_hint?: 'signup' }): Promise<void> {
      // Generate random state
      const state = crypto.randomUUID()
      localStorage.setItem('state', state)

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: AUTH0_CLIENT_ID,
        redirect_uri: REDIRECT_URI,
        scope: 'openid profile email',
        state: state,
        ...(options?.email && { login_hint: options.email }),
        ...(options?.screen_hint && { screen_hint: options.screen_hint })
      })

      window.location.href = `https://${AUTH0_DOMAIN}/authorize?${params.toString()}`
    },

    async logout(): Promise<void> {
      this.user = null
      this.accessToken = null
      localStorage.removeItem('token')
      
      analyticsService.reset()

      try {
        await authService.logout()
      } catch (error) {
        console.warn(error)
      } finally {
        window.location.href = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(window.location.origin)}`
      }
    },

    async handleAuth0Callback(code: string, state: string): Promise<void> {
      const storedState = localStorage.getItem('state')

      // Validate state parameter
      if (state !== storedState) {
        throw new SystemError({
          name: 'AuthenticationError',
          message: 'Unable to complete authentication',
          detail: 'Invalid state parameter - possible security breach attempt'
        })
      }

      const data = await authService.authorizeAuth0(code)
      this.user = data.user
      this.accessToken = data.access

      // Mixpanel identify
      analyticsService.identify(data.user.id!.toString())

      localStorage.setItem('token', data.access)
      localStorage.removeItem('state') // Clear state after successful auth
    },

    async authorizeMicrosoft(): Promise<void> {
      const state = crypto.randomUUID()
      localStorage.setItem('microsoft_state', state)

      const params = new URLSearchParams({
        client_id: MICROSOFT_CLIENT_ID,
        response_type: 'code',
        redirect_uri: MICROSOFT_REDIRECT_URI,
        response_mode: 'query',
        scope: 'openid profile email',
        state: state,
      })

      window.location.href = `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID}/oauth2/v2.0/authorize?${params.toString()}`
    },

    async handleMicrosoftCallback(code: string, state: string): Promise<void> {
      const storedState = localStorage.getItem('microsoft_state')

      if (state !== storedState) {
        throw new SystemError({
          name: 'AuthenticationError',
          message: 'Unable to complete authentication',
          detail: 'Invalid state parameter - possible security breach attempt'
        })
      }

      const data = await authService.authorizeMicrosoft(code, MICROSOFT_REDIRECT_URI)
      this.user = data.user
      this.accessToken = data.access

      analyticsService.identify(data.user.id!.toString())

      localStorage.setItem('token', data.access)
      localStorage.removeItem('microsoft_state')
    },

    // Action method to fetch currently authenticated user
    async fetchUser(): Promise<void> {
      this.user = await authService.fetchAuthUser()
    },

    // Action method to update currently authenticated user
    async updateUser(payload: IUserData): Promise<void> {
      this.user = await authService.updateAuthUser(payload)
      analyticsService.people_set({
        $email: this.user.email,
        $name: `${this.user.first_name} ${this.user.last_name}`,
        organization: this.user.organization?.name,
      })
    },

    async changePassword(new_password1: string, new_password2: string): Promise<void> {
      await authService.changePassword(new_password1, new_password2)
    }
  }
})
